import React from 'react';
import { Button, Checkbox, CircularProgress, FormControlLabel, FormGroup } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DraftsIcon from '@mui/icons-material/Drafts';
import PropTypes from 'prop-types';
import styles from './styles';
import {
  renderDisclaimerContent,
  renderNotesContent,
  renderSubHeadline,
} from '../service/CreatePromotionService';
import { NOTIFY_EMAIL_TEXT } from '../constants';
import { usePromotionPreview } from '../hooks/usePromotionPreview';

const PromotionPreview = ({
  data,
  promotionData,
  flipValue = false,
  mode,
  isAdmin,
  promotionStatus,
  showBtnLoader = false,
  handleCreatePromotion,
  handleSavePromotion,
  handleDraftPromotion,
  handlePublishPromotion,
  disableDRAFTBtn = true,
  disableUPDATEBtn = true,
  selectedStores,
  _defaultEndDate,
  _defaultStartDate,
  selectedConjunction,
  handleNotifyEmail,
  notifyEmail,
  showDraftBtnLoader = false,
  showUpdateBtnLoader = false,
}) => {
  const { headline, disclaimerData, notes, isButtonDisabled } = usePromotionPreview({
    data,
    promotionData,
    flipValue,
    selectedConjunction,
    disableDRAFTBtn,
    disableUPDATEBtn,
    selectedStores,
  });

  return (
    <div style={styles.offerPreview}>
      <h2 style={styles.headline}>{headline}</h2>
      {!!notes && renderNotesContent(notes, true)}
      <div>
        <h4>Subheadline</h4>
        {renderSubHeadline(data?.sub_headline?.FINANCE, flipValue)}
      </div>
      <div style={styles.disclaimerPreview}>
        <h4>Disclaimer</h4>
        {disclaimerData !== null && renderDisclaimerContent(disclaimerData, flipValue)}
      </div>
      <div style={styles.action}>
        {mode === 'Create' && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              disabled={isButtonDisabled('create') || showBtnLoader}
              style={{ marginRight: 8, width: '116px' }}
              variant="contained"
              color="primary"
              onClick={handleCreatePromotion}
              startIcon={
                showBtnLoader ? (
                  <CircularProgress size="1rem" style={{ color: 'black' }} />
                ) : (
                  <AddCircleIcon />
                )
              }
            >
              Create
            </Button>
          </div>
        )}
        {mode !== 'Create' && promotionStatus === 'PUBLISHED' && (
          <div
            style={{
              display: 'flex',
              justifyContent: isAdmin ? 'space-between' : 'center',
              alignItems: 'center',
            }}
          >
            {isAdmin && (
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={notifyEmail} onChange={handleNotifyEmail} />}
                  label={NOTIFY_EMAIL_TEXT}
                  disabled={showBtnLoader || showDraftBtnLoader || showUpdateBtnLoader}
                />
              </FormGroup>
            )}
            <Button
              disabled={
                isButtonDisabled('update') ||
                showBtnLoader ||
                showUpdateBtnLoader ||
                showDraftBtnLoader
              }
              style={{ marginRight: 8, width: '116px' }}
              variant="contained"
              color="primary"
              onClick={handleSavePromotion}
              startIcon={
                showUpdateBtnLoader ? (
                  <CircularProgress size="1rem" style={{ color: 'black' }} />
                ) : (
                  <SaveIcon />
                )
              }
            >
              Update
            </Button>
          </div>
        )}
        {mode !== 'Create' && promotionStatus !== 'PUBLISHED' && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {isAdmin && (
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={notifyEmail} onChange={handleNotifyEmail} />}
                  label={NOTIFY_EMAIL_TEXT}
                  disabled={
                    showBtnLoader ||
                    showDraftBtnLoader ||
                    showUpdateBtnLoader ||
                    promotionStatus === 'ARCHIVED'
                  }
                />
              </FormGroup>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: isAdmin ? 'flex-end' : 'center',
                flexGrow: 1,
              }}
            >
              <Button
                disabled={
                  isButtonDisabled('publish') ||
                  showBtnLoader ||
                  promotionStatus === 'ARCHIVED' ||
                  showUpdateBtnLoader ||
                  showDraftBtnLoader
                }
                style={{ marginRight: 8, width: '116px' }}
                variant="contained"
                color="primary"
                onClick={handlePublishPromotion}
                startIcon={
                  showBtnLoader ? (
                    <CircularProgress size="1rem" style={{ color: 'black' }} />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
              >
                Publish
              </Button>
              {promotionStatus !== 'DRAFT' && (
                <Button
                  disabled={
                    isButtonDisabled('draft') ||
                    showBtnLoader ||
                    showDraftBtnLoader ||
                    showUpdateBtnLoader ||
                    promotionStatus === 'ARCHIVED'
                  }
                  style={{ marginRight: 8, width: '116px' }}
                  variant="contained"
                  color="primary"
                  onClick={handleDraftPromotion}
                  startIcon={
                    showDraftBtnLoader ? (
                      <CircularProgress size="1rem" style={{ color: 'black' }} />
                    ) : (
                      <DraftsIcon />
                    )
                  }
                >
                  Draft
                </Button>
              )}

              {promotionStatus === 'DRAFT' && (
                <Button
                  disabled={
                    isButtonDisabled('update') ||
                    showBtnLoader ||
                    showDraftBtnLoader ||
                    showUpdateBtnLoader
                  }
                  style={{ marginRight: 8, width: '116px' }}
                  variant="contained"
                  color="primary"
                  onClick={handleSavePromotion}
                  startIcon={
                    showUpdateBtnLoader ? (
                      <CircularProgress size="1rem" style={{ color: 'black' }} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                >
                  Update
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

PromotionPreview.propTypes = {
  data: PropTypes.object,
  promotionData: PropTypes.object.isRequired,
  flipValue: PropTypes.bool,
  mode: PropTypes.oneOf(['Create', 'Edit']).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  promotionStatus: PropTypes.string.isRequired,
  showBtnLoader: PropTypes.bool,
  handleCreatePromotion: PropTypes.func.isRequired,
  handleSavePromotion: PropTypes.func.isRequired,
  handleDraftPromotion: PropTypes.func.isRequired,
  handlePublishPromotion: PropTypes.func.isRequired,
  disableDRAFTBtn: PropTypes.bool,
  disableUPDATEBtn: PropTypes.bool,
  selectedStores: PropTypes.array.isRequired,
  _defaultEndDate: PropTypes.string,
  _defaultStartDate: PropTypes.string,
  selectedConjunction: PropTypes.string.isRequired,
  handleNotifyEmail: PropTypes.func.isRequired,
  notifyEmail: PropTypes.bool.isRequired,
  showDraftBtnLoader: PropTypes.bool,
  showUpdateBtnLoader: PropTypes.bool,
};

export default PromotionPreview;
